/*
 * Purpose: Authentic split-flap cell — center-hinged halves + failed-letter bounce retry
 * Ref: https://en.wikipedia.org/wiki/Split-flap_display
 * Last modified: 2026-06-28 | Agent
 * Completeness: 100
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

const STAGGER_MS = 36;
const BOTTOM_FLIP_MS = 220;
const TOP_FLIP_MS = 220;
const FLIP_EASE = [0.42, 0.02, 0.38, 1] as const;
const VERIFY_PAD_MS = 90;
const RETRY_STAGGER_MS = 32;
const RETRY_BOUNCE_MS = 380;
const MAX_RETRIES = 2;

function FlapHalf({ char, half }: { char: string; half: 'top' | 'bottom' }) {
  return (
    <span className={`ab-flap-half ab-flap-half--${half}`}>
      <span className="ab-flap-glyph-wrap">{char}</span>
    </span>
  );
}

export function FlapIdleChar({ char }: { char: string }) {
  return <span className="ab-flap-idle">{char}</span>;
}

export interface SplitFlapSlotProps {
  char: string;
  slotIndex: number;
  col: number;
  tick: number;
  rowVisibleLength: number;
}

type FlipPhase = 'idle' | 'bottom' | 'top';

function flipDeadlineMs(slotIndex: number): number {
  return slotIndex * STAGGER_MS + BOTTOM_FLIP_MS + TOP_FLIP_MS + VERIFY_PAD_MS;
}

export function SplitFlapSlot({
  char,
  slotIndex,
  col,
  tick,
  rowVisibleLength,
}: SplitFlapSlotProps) {
  const glyph = char === ' ' ? '' : char;
  const isTrailingPad = char === ' ' && col >= rowVisibleLength;
  const isWordGap = char === ' ' && !isTrailingPad;

  const [displayChar, setDisplayChar] = useState(glyph);
  const [targetChar, setTargetChar] = useState(glyph);
  const [phase, setPhase] = useState<FlipPhase>('idle');
  const [flipKey, setFlipKey] = useState(0);
  const [bouncing, setBouncing] = useState(false);
  const booted = useRef(false);
  const displayRef = useRef(glyph);
  const phaseRef = useRef<FlipPhase>('idle');
  const flipGen = useRef(0);
  const retryCount = useRef(0);
  const verifyTimer = useRef<number | null>(null);
  const startTimer = useRef<number | null>(null);
  const bounceTimer = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (verifyTimer.current != null) {
      window.clearTimeout(verifyTimer.current);
      verifyTimer.current = null;
    }
    if (startTimer.current != null) {
      window.clearTimeout(startTimer.current);
      startTimer.current = null;
    }
    if (bounceTimer.current != null) {
      window.clearTimeout(bounceTimer.current);
      bounceTimer.current = null;
    }
  }, []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    displayRef.current = displayChar;
  }, [displayChar]);

  const finishFlip = useCallback(() => {
    displayRef.current = targetChar;
    setDisplayChar(targetChar);
    setPhase('idle');
    if (displayRef.current === glyph) {
      retryCount.current = 0;
    }
  }, [targetChar, glyph]);

  const needsRetry = useCallback(
    () => displayRef.current !== glyph || phaseRef.current !== 'idle',
    [glyph]
  );

  const beginFlip = useCallback(
    (gen: number, delayMs: number) => {
      setTargetChar(glyph);
      setPhase('idle');
      setFlipKey(gen);

      startTimer.current = window.setTimeout(() => {
        if (flipGen.current !== gen) return;
        setPhase('bottom');
      }, delayMs);
    },
    [glyph]
  );

  const runBounceRetryRef = useRef<(gen: number) => void>(() => {});

  const scheduleVerify = useCallback(
    (gen: number) => {
      verifyTimer.current = window.setTimeout(() => {
        if (flipGen.current !== gen) return;
        if (needsRetry()) {
          runBounceRetryRef.current(gen);
        }
      }, flipDeadlineMs(slotIndex));
    },
    [needsRetry, slotIndex]
  );

  const runBounceRetry = useCallback(
    (gen: number) => {
      if (flipGen.current !== gen) return;
      if (retryCount.current >= MAX_RETRIES) {
        displayRef.current = glyph;
        setDisplayChar(glyph);
        setTargetChar(glyph);
        setPhase('idle');
        setBouncing(false);
        return;
      }
      retryCount.current += 1;
      setBouncing(true);

      bounceTimer.current = window.setTimeout(() => {
        if (flipGen.current !== gen) return;
        beginFlip(gen, 0);
        scheduleVerify(gen);
      }, slotIndex * RETRY_STAGGER_MS);
    },
    [beginFlip, glyph, scheduleVerify, slotIndex]
  );

  useEffect(() => {
    runBounceRetryRef.current = runBounceRetry;
  }, [runBounceRetry]);

  useEffect(() => {
    if (!booted.current) {
      booted.current = true;
      displayRef.current = glyph;
      setDisplayChar(glyph);
      setTargetChar(glyph);
      return;
    }

    if (glyph === displayRef.current) return;

    clearTimers();
    retryCount.current = 0;
    setBouncing(false);

    const gen = ++flipGen.current;
    beginFlip(gen, slotIndex * STAGGER_MS);
    scheduleVerify(gen);

    return clearTimers;
  }, [glyph, tick, slotIndex, beginFlip, scheduleVerify, clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (isTrailingPad) {
    return <span className="ab-flip-pad" aria-hidden />;
  }

  if (isWordGap && phase === 'idle' && !bouncing) {
    return <span className="ab-flip-gap" aria-hidden />;
  }

  const flipping = phase !== 'idle';

  return (
    <motion.span
      className={`ab-flip-cell${isWordGap ? ' ab-flip-cell--gap' : ''}${flipping ? ' ab-flip-cell--flipping' : ''}${bouncing ? ' ab-flip-cell--bounce' : ''}`}
      aria-hidden
      animate={
        bouncing
          ? { y: [0, -5, 2, 0], scale: [1, 1.06, 0.98, 1] }
          : { y: 0, scale: 1 }
      }
      transition={{
        duration: RETRY_BOUNCE_MS / 1000,
        ease: [0.34, 1.45, 0.64, 1],
      }}
      onAnimationComplete={() => {
        if (bouncing && phase === 'idle' && displayRef.current === glyph) {
          setBouncing(false);
        }
      }}
    >
      <span className="ab-flip-cell__hinge" aria-hidden />

      <span className="ab-flap-stage">
        {flipping ? (
          <span key={flipKey} className="ab-flap-flip-stack">
            <span className="ab-flap-rear" aria-hidden>
              <FlapHalf char={targetChar} half="top" />
              <FlapHalf char={targetChar} half="bottom" />
            </span>
            <motion.span
              className="ab-flap-rotor ab-flap-rotor--bottom"
              initial={{ rotateX: 0 }}
              animate={{ rotateX: 90 }}
              transition={{ duration: BOTTOM_FLIP_MS / 1000, ease: FLIP_EASE }}
              onAnimationComplete={() => {
                if (phaseRef.current === 'bottom') setPhase('top');
              }}
            >
              <FlapHalf char={displayChar} half="bottom" />
            </motion.span>
            <motion.span
              className="ab-flap-rotor ab-flap-rotor--top"
              initial={{ rotateX: 0 }}
              animate={{ rotateX: phase === 'top' ? -90 : 0 }}
              transition={{ duration: TOP_FLIP_MS / 1000, ease: FLIP_EASE }}
              onAnimationComplete={() => {
                if (phaseRef.current === 'top') finishFlip();
              }}
            >
              <FlapHalf char={displayChar} half="top" />
            </motion.span>
          </span>
        ) : (
          <FlapIdleChar char={displayChar} />
        )}
      </span>
    </motion.span>
  );
}

export function splitFlapPhraseDurationMs(charCount: number): number {
  const base = charCount * STAGGER_MS + BOTTOM_FLIP_MS + TOP_FLIP_MS + VERIFY_PAD_MS;
  return base + RETRY_STAGGER_MS * 12 + RETRY_BOUNCE_MS;
}
